import { getDownloadURL, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';
import { storage } from '../lib/firebase';

export function capturePath(uid: string, captureId: string, kind: 'full' | 'thumb') {
  return `users/${uid}/captures/${captureId}-${kind}.jpg`;
}

export async function uploadCaptureImage(
  uid: string,
  captureId: string,
  full: Blob,
  thumb: Blob | null,
  onProgress?: (fraction: number) => void,
): Promise<{ storagePath: string; thumbnailPath: string | null; downloadUrl: string }> {
  const fullPath = capturePath(uid, captureId, 'full');
  const fullRef = ref(storage, fullPath);

  // Resumable upload so the photo's progress is a real measurement rather than
  // a spinner that tells the user nothing on a slow lab connection.
  if (onProgress) {
    const task = uploadBytesResumable(fullRef, full, { contentType: 'image/jpeg' });
    await new Promise<void>((resolve, reject) => {
      task.on(
        'state_changed',
        (snap) => onProgress(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
        reject,
        () => resolve(),
      );
    });
  } else {
    await uploadBytes(fullRef, full, { contentType: 'image/jpeg' });
  }

  // Captured while the bytes are in hand. Shared members cannot read the
  // uploader's storage path directly, but they can follow this URL.
  const downloadUrl = await getDownloadURL(fullRef);

  let thumbnailPath: string | null = null;
  if (thumb) {
    thumbnailPath = capturePath(uid, captureId, 'thumb');
    await uploadBytes(ref(storage, thumbnailPath), thumb, { contentType: 'image/jpeg' });
  }
  return { storagePath: fullPath, thumbnailPath, downloadUrl };
}

const urlCache = new Map<string, string>();

/** Download URLs are stable per object; cache them so revisiting history is free. */
export async function resolveImageUrl(path: string): Promise<string> {
  const cached = urlCache.get(path);
  if (cached) return cached;
  const url = await getDownloadURL(ref(storage, path));
  urlCache.set(path, url);
  return url;
}

/**
 * A dictation, on its way to be transcribed.
 *
 * The recording is deleted server-side the moment the words are out, so this is
 * a staging area rather than storage: the transcript is what the capture keeps.
 */
export function audioPath(uid: string, captureId: string, ext: string) {
  return `users/${uid}/captures/${captureId}-audio.${ext}`;
}

export async function uploadRecording(
  uid: string,
  captureId: string,
  blob: Blob,
  mimeType: string,
): Promise<string> {
  const ext = mimeType.includes('ogg') ? 'ogg' : 'webm';
  const path = audioPath(uid, captureId, ext);
  await uploadBytes(ref(storage, path), blob, { contentType: mimeType });
  return path;
}
